/**
 * homeSettings.ts — user-customizable style settings for the i-SYNERGY Home
 * launcher (src/modules/home/Home.tsx).
 *
 * Same mechanism as the Production Grid's gridSettings.ts and the Accounting
 * modules' acctSettings.ts: typed defaults → CSS custom properties set inline
 * on the Home root element → every descendant picks the values up through
 * normal CSS inheritance. Home.css reads every knob as
 * `var(--ish-*, <original literal>)`, so a missing/first-run value renders
 * exactly the pre-settings look, and inline custom properties on the root win
 * over the class defaults.
 *
 * DEFAULT typography follows the Production Grid — one clean system sans stack
 * for the brand, hero, tiles, and footer alike (the reference typography the
 * rest of the app already standardizes on; see acctSettings.ts PG_FONT). The
 * Home previously mixed Fraunces (serif) + JetBrains Mono, neither of which is
 * loaded, so it fell back to Times/Courier — this unifies it to the Production
 * Grid look while keeping the font a first-class, swappable setting.
 *
 * COLORS are opt-in overrides. Home is a DUAL (dark/light) theme — leaving a
 * color blank ('') keeps the active theme's token, so the light/dark toggle
 * keeps working. Only when a value is provided does it override that element.
 * The two global tokens (accent + page background) additionally override the
 * theme token itself, which pins them across the toggle — that is the intended
 * behavior of an explicit global override.
 *
 * Persistence: localStorage (per browser). These are personal display
 * preferences, not shared business data — same call the Accounting settings
 * make. A `subscribe` fan-out lets an open Home apply saved changes instantly.
 */

/** The Production Grid font stack — the app's reference typography. */
export const PG_FONT = '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Selectable font presets for the family dropdowns. */
export const HOME_FONT_PRESETS: { label: string; value: string }[] = [
  { label: 'Production Grid (system sans)', value: PG_FONT },
  { label: 'Inter', value: "'Inter', system-ui, sans-serif" },
  { label: 'DM Sans', value: "'DM Sans', system-ui, sans-serif" },
  { label: 'Hanken Grotesk', value: "'Hanken Grotesk', system-ui, sans-serif" },
  { label: 'Fraunces (serif)', value: "'Fraunces', Georgia, serif" },
  { label: 'JetBrains Mono', value: "'JetBrains Mono', 'Cascadia Mono', Consolas, monospace" },
];

/** Top bar (brand, user chip, icon buttons). */
export interface IHomeHeaderSettings {
  paddingV: number;
  paddingH: number;
  brandFontSize: number;
  /** Brand wordmark color; empty = theme text. */
  brandColor: string;
  userChipFontSize: number;
  /** "INTALGARMENTS" chip color; empty = theme dim. */
  userChipColor: string;
}

/** Centered hero block (eyebrow, i-SYNERGY title, subtitle). */
export interface IHomeHeroSettings {
  paddingTop: number;
  paddingBottom: number;
  eyebrowFontSize: number;
  /** Letter spacing in em for the uppercase eyebrow. */
  eyebrowLetterSpacing: number;
  /** Eyebrow color; empty = theme accent (khaki). */
  eyebrowColor: string;
  titleFontSize: number;
  /** The "i" (leading) part of the wordmark; empty = theme text. */
  titleColor: string;
  /** The "-SYNERGY" (accent) part of the wordmark; empty = theme accent. */
  titleAccentColor: string;
  subtitleFontSize: number;
  /** Sub-headline color; empty = theme dim. */
  subtitleColor: string;
}

/** The Applications area — group label + the app-tile grid ("body"). */
export interface IHomeBodySettings {
  maxWidth: number;
  paddingV: number;
  paddingH: number;
  groupLabelFontSize: number;
  /** "APPLICATIONS" label color; empty = theme faint. */
  groupLabelColor: string;
  gridGap: number;
  /** Minimum tile column width before wrapping. */
  gridMinColWidth: number;
  tilePaddingV: number;
  tilePaddingH: number;
  tileRadius: number;
  /** Tile background; empty = theme tile. */
  tileBackground: string;
  /** Tile border color; empty = theme soft border. */
  tileBorderColor: string;
  iconSize: number;
  iconRadius: number;
  /** Glyph (svg) size inside the icon plate. */
  iconGlyphSize: number;
  /** Vertical gap between the icon and its label. */
  iconLabelGap: number;
  /** Icon plate border thickness (px). 0 hides it. */
  iconBorderWidth: number;
  /** Icon plate border color; empty = theme border. */
  iconBorderColor: string;
  /** Icon plate background; empty = theme surface2. */
  iconBackground: string;
  /** Default glyph (svg stroke) color; empty = theme accent (khaki). */
  iconGlyphColor: string;
  nameFontSize: number;
  /** App name color; empty = theme text. */
  nameColor: string;
  /** Show the app subtitle (the second line under each app name). */
  showAppMeta: boolean;
  /** Show the little module-count badge in each tile's corner ("3 ⌄"). */
  showModuleBadge: boolean;
  metaFontSize: number;
  /** App meta / subtitle color; empty = theme faint. */
  metaColor: string;
}

/** The footer strip. */
export interface IHomeFooterSettings {
  paddingV: number;
  paddingH: number;
  fontSize: number;
  letterSpacing: number;
  /** Footer text color; empty = theme faint. */
  color: string;
}

/** The module drawer (opened when a grouped app tile is tapped). */
export interface IHomeDrawerSettings {
  /** Liquid-glass backdrop blur (px) behind the sheet. */
  glassBlur: number;
  /** Sheet background opacity (0–100) — lower = clearer glass. */
  glassOpacity: number;
  /** Sheet tint color; empty = theme surface. */
  glassTint: string;
  /** Sheet border color; empty = frosted white edge. */
  sheetBorderColor: string;
  /** Sheet corner radius (px). */
  sheetRadius: number;
  /** Sheet max width (px). */
  sheetMaxWidth: number;
  /** Module cards per row. */
  columns: number;
  /** Module card background; empty = frosted glass. */
  itemBackground: string;
  /** Module card border color; empty = frosted white edge. */
  itemBorderColor: string;
  /** Module card corner radius (px). */
  itemRadius: number;
  /** Module icon plate size (px). */
  itemIconSize: number;
  padding: number;
  gridGap: number;
  titleFontSize: number;
  /** Drawer heading color; empty = theme text. */
  titleColor: string;
  subFontSize: number;
  /** "N modules" sub color; empty = theme faint. */
  subColor: string;
  itemPaddingV: number;
  itemPaddingH: number;
  itemNameFontSize: number;
  /** Module name color; empty = theme text. */
  itemNameColor: string;
  itemMetaFontSize: number;
  /** Module meta color; empty = theme faint. */
  itemMetaColor: string;
}

/** Editable text content for the fixed labels on the Home. Any field set to an
 * empty string hides that element entirely (edit or delete every label). */
export interface IHomeTextSettings {
  /** Header wordmark next to the logo ("i-SYNERGY · Home"). */
  brand: string;
  /** Hero eyebrow ("Intal Garments OPC · Digital. Innovate"). */
  eyebrow: string;
  /** Hero title, leading part ("i"). */
  titleLead: string;
  /** Hero title, accent part ("-SYNERGY"). */
  titleAccent: string;
  /** Hero subtitle sentence. */
  subtitle: string;
  /** Group label above the grid ("Applications"). */
  appsLabel: string;
  /** Footer, left side ("INTAL GARMENTS OPC"). */
  footerLeft: string;
  /** Footer, right side ("5 APPS · 4 SYSTEMS · SPFx + AZURE SWA"). */
  footerRight: string;
}

/**
 * The running text in the sidebar, under the signed-in user's name. It sits in
 * the Home settings because that is the app's one SHARED settings row
 * (iSyn_HomeSettings): editing it here changes it for everybody, which is what
 * a company message wants and a per-browser setting would not give.
 */
export interface IHomeTickerSettings {
  /**
   * The message. BLANK means no ticker: the sidebar shows the app version and
   * build time there instead, as it did before this existed — that stamp is how
   * a stale bundle gets told from a bug, so it must always be reachable.
   */
  text: string;
  /** Scroll speed in pixels per second. Clamped on use (see tickerTiming). */
  speed: number;
  /**
   * Run a different quote each day instead of `text` (see dailyQuote.ts). A row
   * saved before this existed has no `quotes`, which reads as off — the message
   * that was set keeps running until somebody turns this on.
   */
  quotes?: boolean;
  /** Own quotes, one per line, replacing the built-in list. Blank = built-in. */
  customQuotes?: string;
}

/** Global knobs — font + the two theme-token overrides + default theme. */
export interface IHomeGlobalSettings {
  fontFamily: string;
  /** Startup theme for the Home. */
  defaultTheme: 'dark' | 'light';
  /** Accent (khaki) override; empty = per-theme default. Pins across toggle. */
  accent: string;
  /** Page background override; empty = per-theme default. Pins across toggle. */
  pageBackground: string;
}

export interface IHomeSettings {
  version: number;
  global: IHomeGlobalSettings;
  header: IHomeHeaderSettings;
  hero: IHomeHeroSettings;
  body: IHomeBodySettings;
  footer: IHomeFooterSettings;
  drawer: IHomeDrawerSettings;
  /** Editable text for the Home's fixed labels; empty = hide. */
  text: IHomeTextSettings;
  /** Running text in the sidebar footer; shared with everybody like the rest. */
  ticker: IHomeTickerSettings;
  /**
   * Per-app custom icon images, keyed by the app's stable id (see APPS in
   * Home.tsx). Value is a data URL (uploaded + downscaled in the settings
   * page) that fully replaces that app's default icon. Empty/absent = the
   * built-in icon. Not a CSS variable — Home.tsx reads it directly.
   */
  icons: Record<string, string>;
  /**
   * Per-app tile-label overrides, keyed by app id. `name`/`meta` when present
   * replace the built-in text; an empty string hides that line. Absent = the
   * built-in label. Not a CSS variable — Home.tsx reads it directly.
   */
  appText: Record<string, { name?: string; meta?: string }>;
}

/**
 * Every default below mirrors the original hardcoded value in Home.css, so
 * with no saved settings the Home renders byte-for-byte as before — EXCEPT
 * the font family, which now defaults to the Production Grid stack (the
 * requested change) instead of the never-loaded Fraunces/JetBrains Mono.
 */
export const DEFAULT_HOME_SETTINGS: IHomeSettings = {
  version: 2,
  global: {
    fontFamily: PG_FONT,
    defaultTheme: 'dark',
    accent: '',
    pageBackground: '',
  },
  header: {
    paddingV: 14,
    paddingH: 28,
    brandFontSize: 18,
    brandColor: '',
    userChipFontSize: 12.5,
    userChipColor: '',
  },
  hero: {
    paddingTop: 54,
    paddingBottom: 8,
    eyebrowFontSize: 11,
    eyebrowLetterSpacing: 0.34,
    eyebrowColor: '',
    titleFontSize: 48,
    titleColor: '',
    titleAccentColor: '',
    subtitleFontSize: 14.5,
    subtitleColor: '',
  },
  body: {
    maxWidth: 880,
    paddingV: 40,
    paddingH: 28,
    groupLabelFontSize: 11,
    groupLabelColor: '',
    gridGap: 14,
    gridMinColWidth: 150,
    tilePaddingV: 24,
    tilePaddingH: 16,
    tileRadius: 18,
    tileBackground: '',
    tileBorderColor: '',
    iconSize: 62,
    iconRadius: 16,
    iconGlyphSize: 30,
    iconLabelGap: 12,
    iconBorderWidth: 2,
    iconBorderColor: '',
    iconBackground: '',
    iconGlyphColor: '',
    nameFontSize: 14,
    nameColor: '',
    showAppMeta: false,
    showModuleBadge: false,
    metaFontSize: 10.5,
    metaColor: '',
  },
  footer: {
    paddingV: 22,
    paddingH: 28,
    fontSize: 11,
    letterSpacing: 0.05,
    color: '',
  },
  drawer: {
    glassBlur: 22,
    glassOpacity: 55,
    glassTint: '',
    sheetBorderColor: '',
    sheetRadius: 26,
    sheetMaxWidth: 520,
    columns: 2,
    itemBackground: '',
    itemBorderColor: '',
    itemRadius: 16,
    itemIconSize: 50,
    padding: 28,
    gridGap: 12,
    titleFontSize: 21,
    titleColor: '',
    subFontSize: 11,
    subColor: '',
    itemPaddingV: 20,
    itemPaddingH: 16,
    itemNameFontSize: 13.5,
    itemNameColor: '',
    itemMetaFontSize: 10,
    itemMetaColor: '',
  },
  text: {
    brand: 'i-SYNERGY · Home',
    eyebrow: 'Intal Garments OPC · Digital. Innovate',
    titleLead: 'i',
    titleAccent: '-SYNERGY',
    subtitle: 'One home for every platform. Tap an app to launch — grouped apps open their modules.',
    appsLabel: 'Applications',
    footerLeft: 'INTAL GARMENTS OPC',
    footerRight: '5 APPS · 4 SYSTEMS · SPFx + AZURE SWA',
  },
  // The Home's own tagline, so the ticker has something of the company's to run
  // until somebody writes the real message. Saved blank, it is turned off.
  ticker: { text: 'Intal Garments OPC · Digital. Innovate', speed: 30, quotes: true, customQuotes: '' },
  icons: {},
  appText: {},
};

// ─── Persistence + change notification ──────────────────────────────────────

const STORAGE_KEY = 'ish:home-settings';
const listeners = new Set<(s: IHomeSettings) => void>();

function deepMerge<T>(base: T, patch: Partial<T> | undefined): T {
  if (!patch) return base;
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(patch)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = deepMerge((base as Record<string, unknown>)[k] ?? {}, v as Record<string, unknown>);
    } else if (v !== undefined) {
      out[k] = v;
    }
  }
  return out as T;
}

/** Deep-merge a partial settings patch (e.g. the shared config from SharePoint)
 * onto a base settings object. */
export function mergeHomeSettings(base: IHomeSettings, patch: Partial<IHomeSettings> | undefined): IHomeSettings {
  return deepMerge(base, patch);
}

export function loadHomeSettings(): IHomeSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_HOME_SETTINGS;
    const saved = JSON.parse(raw) as Partial<IHomeSettings> & { version?: number };
    const savedVersion = saved.version ?? 1;
    // ── v1 → v2 migration ──────────────────────────────────────────────────
    // The icon border default changed from 0 (no border) to 2px khaki. A saved
    // v1 border width of 0 was the OLD default, not a deliberate choice — drop
    // it so the khaki frame applies. A 0 set deliberately under v2 is kept.
    if (savedVersion < 2 && saved.body && saved.body.iconBorderWidth === 0) {
      delete (saved.body as unknown as Record<string, unknown>).iconBorderWidth;
    }
    // Merge over defaults so settings saved by an older version stay valid when
    // new knobs are added; stamp the current version so migrations run once.
    const merged = deepMerge(DEFAULT_HOME_SETTINGS, saved);
    merged.version = DEFAULT_HOME_SETTINGS.version;
    return merged;
  } catch {
    return DEFAULT_HOME_SETTINGS;
  }
}

export function saveHomeSettings(s: IHomeSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* storage full / disabled — settings still apply for this session */
  }
  for (const fn of listeners) fn(s);
}

export function resetHomeSettings(): IHomeSettings {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  for (const fn of listeners) fn(DEFAULT_HOME_SETTINGS);
  return DEFAULT_HOME_SETTINGS;
}

export function subscribeHomeSettings(fn: (s: IHomeSettings) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// ─── CSS variables ───────────────────────────────────────────────────────────

/**
 * Inline style object for the Home root element (`.ish-root`). Size/spacing
 * knobs are always emitted; per-element color overrides are emitted only when
 * non-empty (so a blank value cleanly falls through to the active theme token
 * via `var(--ish-x, var(--themeToken))` in Home.css). The two global
 * overrides (accent, page background) additionally override the theme token
 * itself when set.
 */
export function homeSettingsToCssVars(s: IHomeSettings): Record<string, string> {
  const px = (n: number) => `${n}px`;
  const em = (n: number) => `${n}em`;
  const vars: Record<string, string> = {
    // ── Global ──
    '--ish-font-family': s.global.fontFamily,

    // ── Header ──
    '--ish-header-pad-v': px(s.header.paddingV),
    '--ish-header-pad-h': px(s.header.paddingH),
    '--ish-brand-size': px(s.header.brandFontSize),
    '--ish-userchip-size': px(s.header.userChipFontSize),

    // ── Hero ──
    '--ish-hero-pad-top': px(s.hero.paddingTop),
    '--ish-hero-pad-bottom': px(s.hero.paddingBottom),
    '--ish-eyebrow-size': px(s.hero.eyebrowFontSize),
    '--ish-eyebrow-ls': em(s.hero.eyebrowLetterSpacing),
    '--ish-title-size': px(s.hero.titleFontSize),
    '--ish-subtitle-size': px(s.hero.subtitleFontSize),

    // ── Body / Applications ──
    '--ish-body-maxw': px(s.body.maxWidth),
    '--ish-body-pad-v': px(s.body.paddingV),
    '--ish-body-pad-h': px(s.body.paddingH),
    '--ish-grouplabel-size': px(s.body.groupLabelFontSize),
    '--ish-grid-gap': px(s.body.gridGap),
    '--ish-grid-mincol': px(s.body.gridMinColWidth),
    '--ish-tile-pad-v': px(s.body.tilePaddingV),
    '--ish-tile-pad-h': px(s.body.tilePaddingH),
    '--ish-tile-radius': px(s.body.tileRadius),
    '--ish-icon-size': px(s.body.iconSize),
    '--ish-icon-radius': px(s.body.iconRadius),
    '--ish-icon-glyph': px(s.body.iconGlyphSize),
    '--ish-icon-label-gap': px(s.body.iconLabelGap),
    '--ish-icon-border-width': px(s.body.iconBorderWidth),
    '--ish-name-size': px(s.body.nameFontSize),
    '--ish-meta-size': px(s.body.metaFontSize),

    // ── Footer ──
    '--ish-footer-pad-v': px(s.footer.paddingV),
    '--ish-footer-pad-h': px(s.footer.paddingH),
    '--ish-footer-size': px(s.footer.fontSize),
    '--ish-footer-ls': em(s.footer.letterSpacing),

    // ── Drawer (liquid glass) ──
    '--ish-drawer-blur': px(s.drawer.glassBlur),
    // The sheet background: the tint (or theme surface) at the chosen opacity —
    // backdrop blur underneath makes it read as frosted glass.
    '--ish-drawer-bg': `color-mix(in srgb, ${s.drawer.glassTint.trim() || 'var(--surface)'} ${Math.min(100, Math.max(0, s.drawer.glassOpacity))}%, transparent)`,
    '--ish-drawer-radius': px(s.drawer.sheetRadius),
    '--ish-drawer-maxw': px(s.drawer.sheetMaxWidth),
    '--ish-drawer-cols': String(Math.max(1, Math.round(s.drawer.columns))),
    '--ish-drawer-item-radius': px(s.drawer.itemRadius),
    '--ish-drawer-icon-size': px(s.drawer.itemIconSize),
    '--ish-drawer-pad': px(s.drawer.padding),
    '--ish-drawer-gap': px(s.drawer.gridGap),
    '--ish-drawer-title-size': px(s.drawer.titleFontSize),
    '--ish-drawer-sub-size': px(s.drawer.subFontSize),
    '--ish-drawer-item-pad-v': px(s.drawer.itemPaddingV),
    '--ish-drawer-item-pad-h': px(s.drawer.itemPaddingH),
    '--ish-drawer-item-name-size': px(s.drawer.itemNameFontSize),
    '--ish-drawer-item-meta-size': px(s.drawer.itemMetaFontSize),
  };

  // Per-element color overrides — only emit when the user set one, otherwise
  // Home.css's `var(--ish-x, var(--themeToken))` fallback keeps the theme color.
  const put = (name: string, value: string) => {
    if (value && value.trim()) vars[name] = value.trim();
  };
  put('--ish-brand-color', s.header.brandColor);
  put('--ish-userchip-color', s.header.userChipColor);
  put('--ish-eyebrow-color', s.hero.eyebrowColor);
  put('--ish-title-color', s.hero.titleColor);
  put('--ish-title-accent-color', s.hero.titleAccentColor);
  put('--ish-subtitle-color', s.hero.subtitleColor);
  put('--ish-grouplabel-color', s.body.groupLabelColor);
  put('--ish-tile-bg', s.body.tileBackground);
  put('--ish-tile-border', s.body.tileBorderColor);
  put('--ish-icon-border-color', s.body.iconBorderColor);
  put('--ish-icon-bg', s.body.iconBackground);
  put('--ish-icon-glyph-color', s.body.iconGlyphColor);
  put('--ish-name-color', s.body.nameColor);
  put('--ish-meta-color', s.body.metaColor);
  put('--ish-footer-color', s.footer.color);
  put('--ish-drawer-title-color', s.drawer.titleColor);
  put('--ish-drawer-sub-color', s.drawer.subColor);
  put('--ish-drawer-item-name-color', s.drawer.itemNameColor);
  put('--ish-drawer-item-meta-color', s.drawer.itemMetaColor);
  put('--ish-drawer-border', s.drawer.sheetBorderColor);
  put('--ish-drawer-item-bg', s.drawer.itemBackground);
  put('--ish-drawer-item-border', s.drawer.itemBorderColor);

  // Global theme-token overrides — these DO override the theme's own token, so
  // they pin across the light/dark toggle (intended for an explicit override).
  if (s.global.accent && s.global.accent.trim()) {
    const a = s.global.accent.trim();
    vars['--khaki'] = a;
    vars['--khaki-lt'] = a;
    vars['--khaki-soft'] = `color-mix(in srgb, ${a} 13%, transparent)`;
    vars['--khaki-bd'] = `color-mix(in srgb, ${a} 32%, transparent)`;
  }
  if (s.global.pageBackground && s.global.pageBackground.trim()) {
    vars['--page'] = s.global.pageBackground.trim();
  }

  return vars;
}
